import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Flex, Text, Modal, Alert, Spin, Button, message } from '@nula/core-ui';
import {
  useSubscription,
  useCreditBalance,
  useCreateCheckoutSession,
  useCreatePortalSession,
  useResumeSubscription,
  useChangePlan,
  useInvalidateBilling,
  useAwaitPlanActivation,
} from '@/lib/billing/use-subscription';
import { BillingStatusCard } from '@/lib/billing/billing-status-card';
import { PlanCatalog } from '@/lib/billing/plan-catalog';
import { ChangePlanConfirm } from '@/lib/billing/change-plan-confirm';
import { StripeElementsProvider } from '@/lib/billing/stripe-provider';
import { SubscribePanel } from '@/lib/billing/subscribe-panel';
import { CancelRetentionModal } from '@/lib/billing/cancel-retention-modal';
import { ReferralSection } from '@/lib/billing/referral-section';
import { checkoutRejectionFromError } from '@/lib/billing/checkout-rejection';
import { hasSavedCard } from '@/lib/billing/plan-cta';
import type { CheckoutRejection } from '@/lib/billing/promo-code-input';
import type { BillingInterval, PlanCatalogEntry } from '@/lib/billing/types';
import { openExternalUrl } from '@/utils/open-external-url';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import styles from './subscription-tab.module.css';

export function SubscriptionTab() {
  // The account-level subscription tab is ALWAYS about the personal workspace,
  // regardless of which workspace is currently active — otherwise a team being
  // active would make this tab show the team's subscription.
  const personalWorkspaceId = useWorkspaceStore(
    (s) => s.workspaces.find((w) => w.type === 'personal')?.id ?? null,
  );
  const wsId = personalWorkspaceId ?? undefined;
  const { data: subscription, isLoading } = useSubscription(wsId);
  const { data: credits } = useCreditBalance(wsId);
  const checkout = useCreateCheckoutSession(wsId);
  const portal = useCreatePortalSession(wsId);
  const resume = useResumeSubscription(wsId);
  const changePlan = useChangePlan(wsId);
  const invalidateBilling = useInvalidateBilling(wsId);
  const activation = useAwaitPlanActivation(wsId);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<{
    plan: PlanCatalogEntry;
    interval: BillingInterval;
  } | null>(null);
  const [checkoutRejection, setCheckoutRejection] = useState<CheckoutRejection | null>(null);
  const rejectionNonce = useRef(0);

  // A workspace with no paid subscription (Basic, or a canceled/lapsed self-serve
  // workspace) sees the plan catalog as the whole tab — the pricing IS the point.
  // A paid workspace sees its status card, and can open the catalog via "Change
  // plan" (which swaps in place, with a Back to return).
  const onFreeTier =
    !!subscription &&
    subscription.isSelfServe &&
    (subscription.planCode === 'basic' || subscription.status === 'canceled');
  // While a paid checkout is waiting on invoice.paid the server still reports
  // the OLD plan, so the catalog would re-render "Basic — Current plan" over a
  // payment that did go through. Show the waiting state instead of the catalog.
  // A timed-out poll is a dead end (nothing keeps refetching), so unlike the
  // catalog, the status card stays visible underneath the notice — otherwise a
  // paid customer loses every billing control until a full reload.
  const awaitingActivation = activation.state === 'pending' || activation.state === 'timed_out';
  const showCatalog = (onFreeTier || changingPlan) && !awaitingActivation;

  // A workspace with NO live paid Stripe subscription subscribes via first-time
  // checkout (Payment Element). A trial counts as unbound ONLY while it has no
  // card: once a card is saved the Stripe subscription is live, and checkout
  // answers 409 already_subscribed — which surfaces as a generic retryable
  // error, so such a user must go through change-plan instead.
  const noLiveSubscription =
    !!subscription &&
    (subscription.planCode === 'basic' ||
      (subscription.status === 'trialing' && !hasSavedCard(subscription)) ||
      subscription.status === 'canceled');

  const handleSelectPlan = async (plan: PlanCatalogEntry, interval: BillingInterval, promotionCode?: string) => {
    // Selecting Basic is a downgrade (change-plan schedules a cancel), never a checkout.
    if (noLiveSubscription && plan.code !== 'basic') {
      try {
        const res = await checkout.mutateAsync({ planCode: plan.code, interval, promotionCode });
        setChangingPlan(false);
        setPendingSwitch(null);
        setClientSecret(res.clientSecret);
      } catch (err) {
        Sentry.captureException(err);
        rejectionNonce.current += 1;
        const rejection = checkoutRejectionFromError(err, rejectionNonce.current);
        if (rejection) {
          setCheckoutRejection(rejection);
        } else {
          message.error("Couldn't start checkout. Please try again.");
        }
      }
      return;
    }
    if (plan.code === 'basic') {
      await commitPlanChange(plan, interval);
      return;
    }
    // Live paid sub switching to another PAID tier: confirm the proration first.
    setPendingSwitch({ plan, interval });
  };

  const commitPlanChange = async (plan: PlanCatalogEntry, interval: BillingInterval) => {
    try {
      await changePlan.mutateAsync({ planCode: plan.code, interval });
      setPendingSwitch(null);
      setChangingPlan(false);
      await invalidateBilling();
      message.success(plan.code === 'basic' ? 'Your plan will change to Basic.' : `Switched to ${plan.name}.`);
    } catch (err) {
      Sentry.captureException(err);
      message.error("Couldn't change your plan. Please try again.");
    }
  };

  const handlePortal = async () => {
    try {
      const res = await portal.mutateAsync();
      await openExternalUrl(res.url);
    } catch (err) {
      Sentry.captureException(err);
      message.error("Couldn't open billing. Please try again.");
    }
  };

  const handleResume = async () => {
    try {
      await resume.mutateAsync();
      message.success('Your plan will continue.');
    } catch (err) {
      Sentry.captureException(err);
      message.error("Couldn't resume your plan. Please try again.");
    }
  };

  const handleCheckActivationAgain = async () => {
    await invalidateBilling();
    activation.reset();
  };

  return (
    <Flex vertical gap="lg">
      {/* When a paid user swaps into the catalog via "Change plan", the catalog
          shows its own Back + "Choose your plan" heading — so the tab's own
          Subscription header/subtitle would be a redundant second heading. Hide
          it in that case; keep it for the default status card and the free-tier
          catalog. */}
      {!changingPlan && (
        <Flex vertical gap="xs">
          <Text size="md" className={styles.title}>
            Subscription
          </Text>
          <Text size="sm" type="secondary">
            Manage your billing and plan details.
          </Text>
        </Flex>
      )}

      {activation.state === 'pending' && (
        <Flex gap="sm" align="center" data-testid="activation-pending">
          <Spin size="sm" />
          <Text size="sm">Confirming your payment. This usually takes a few seconds.</Text>
        </Flex>
      )}

      {activation.state === 'timed_out' && (
        <Alert
          type="info"
          showIcon
          data-testid="activation-timed-out"
          message="Your payment is still processing"
          description="Your card was submitted successfully. Your new plan will appear here as soon as it's confirmed — there's no need to pay again. Contact support if it hasn't updated in a few minutes."
          action={
            <Button variant="text" size="sm" onClick={() => void handleCheckActivationAgain()}>
              Check again
            </Button>
          }
        />
      )}

      {activation.state === 'pending' ? null : subscription && showCatalog ? (
        <PlanCatalog
          subscription={subscription}
          onSelectPlan={(plan, interval, promotionCode) => void handleSelectPlan(plan, interval, promotionCode)}
          onBack={changingPlan ? () => setChangingPlan(false) : undefined}
          checkoutRejection={checkoutRejection}
          workspaceId={wsId}
        />
      ) : (
        <BillingStatusCard
          subscription={subscription}
          credits={credits}
          loading={isLoading}
          onSubscribe={() => setChangingPlan(true)}
          onChangePlan={() => setChangingPlan(true)}
          onManageBilling={() => void handlePortal()}
          onCancel={() => setCancelOpen(true)}
          onResume={() => void handleResume()}
        />
      )}

      <Modal
        open={clientSecret !== null}
        onClose={() => setClientSecret(null)}
        title="Upgrade to Nula Pro"
        footer={null}
        centered
      >
        {clientSecret && (
          <StripeElementsProvider clientSecret={clientSecret}>
            <SubscribePanel
              clientSecret={clientSecret}
              onSuccess={() => {
                setClientSecret(null);
                setChangingPlan(false);
                message.success('Payment received. Confirming your plan…');
                if (subscription) {
                  activation.start({
                    planCode: subscription.planCode,
                    interval: subscription.interval,
                    status: subscription.status,
                  });
                }
              }}
            />
          </StripeElementsProvider>
        )}
      </Modal>

      <CancelRetentionModal
        open={cancelOpen}
        workspaceId={wsId}
        workspaceType={subscription?.workspaceType}
        planName={subscription?.planName ?? 'Pro'}
        onClose={() => setCancelOpen(false)}
        onCanceled={(effectiveAt) => {
          setCancelOpen(false);
          message.info(
            effectiveAt
              ? `Your plan will end on ${new Date(effectiveAt).toLocaleDateString()}.`
              : 'Your plan will end at the end of the current billing period.',
          );
        }}
      />

      {pendingSwitch && (
        <ChangePlanConfirm
          open
          workspaceId={wsId}
          planCode={pendingSwitch.plan.code}
          planName={pendingSwitch.plan.name}
          interval={pendingSwitch.interval}
          onClose={() => setPendingSwitch(null)}
          onConfirm={() => commitPlanChange(pendingSwitch.plan, pendingSwitch.interval)}
        />
      )}

      {!changingPlan && <ReferralSection />}
    </Flex>
  );
}
